import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, setDoc, query, where, writeBatch } from 'firebase/firestore';
import { db } from './firebaseConfig';

const educationalInstitutionsData = {
  // Obtener todas las instituciones
  getAllInstitutions: async () => {
    const querySnapshot = await getDocs(collection(db, 'educationalInstitutions'));
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Obtener una institución por su ID
  getInstitutionById: async (institutionId) => {
    const institutionRef = doc(db, 'educationalInstitutions', institutionId);
    const institutionSnap = await getDoc(institutionRef);
    if (institutionSnap.exists()) {
      return { id: institutionSnap.id, ...institutionSnap.data() };
    } else {
      throw new Error('Institución no encontrada');
    }
  },

  // Crear una nueva institución
  createInstitution: async (institutionData) => {
    try {
      const docRef = await addDoc(collection(db, 'educationalInstitutions'), {
        ...institutionData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      const newInstitution = await getDoc(docRef);
      return { id: newInstitution.id, ...newInstitution.data() };
    } catch (error) {
      console.error('Error al crear la institución:', error);
      throw error;
    }
  },

  // Actualizar una institución existente
  updateInstitution: async (institutionId, updateData) => {
    try {
      const institutionRef = doc(db, 'educationalInstitutions', institutionId);
      await updateDoc(institutionRef, {
        ...updateData,
        updatedAt: new Date().toISOString()
      });
      const updatedInstitution = await getDoc(institutionRef);
      return { id: updatedInstitution.id, ...updatedInstitution.data() };
    } catch (error) {
      console.error('Error al actualizar la institución:', error);
      throw error;
    }
  },

  // Eliminar una institución
  deleteInstitution: async (institutionId) => {
    try {
      const institutionRef = doc(db, 'educationalInstitutions', institutionId);
      await deleteDoc(institutionRef);
      return true;
    } catch (error) {
      console.error('Error al eliminar la institución:', error);
      throw error;
    }
  },

  // Obtener sub-colección de admins
  getAdmins: async (institutionId) => {
    const adminsRef = collection(db, 'educationalInstitutions', institutionId, 'admins');
    const querySnapshot = await getDocs(adminsRef);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Agregar admin
  addAdmin: async (institutionId, adminData) => {
    const adminsRef = collection(db, 'educationalInstitutions', institutionId, 'admins');
    const docRef = await addDoc(adminsRef, adminData);
    return { id: docRef.id, ...adminData };
  },

  // Eliminar admin
  deleteAdmin: async (institutionId, adminId) => {
    const adminRef = doc(db, 'educationalInstitutions', institutionId, 'admins', adminId);
    await deleteDoc(adminRef);
    return true;
  },

  // Obtener sub-colección de mentors
  getMentors: async (institutionId) => {
    const mentorsRef = collection(db, 'educationalInstitutions', institutionId, 'mentors');
    const querySnapshot = await getDocs(mentorsRef);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // Agregar mentor
  addMentor: async (institutionId, mentorData) => {
    const mentorsRef = collection(db, 'educationalInstitutions', institutionId, 'mentors');
    const docRef = await addDoc(mentorsRef, mentorData);
    return { id: docRef.id, ...mentorData };
  },

  // Eliminar mentor
  deleteMentor: async (institutionId, mentorId) => {
    const mentorRef = doc(db, 'educationalInstitutions', institutionId, 'mentors', mentorId);
    await deleteDoc(mentorRef);
    return true;
  },

  // Obtener sub-colección de academyCourses (solo los IDs)
  getAcademyCourses: async (institutionId) => {
    const coursesRef = collection(db, 'educationalInstitutions', institutionId, 'academyCourses');
    const querySnapshot = await getDocs(coursesRef);
    return querySnapshot.docs.map(doc => doc.id);
  },

  // Eliminar un curso de la sub-colección academyCourses
  deleteAcademyCourse: async (institutionId, courseId) => {
    try {
      const batch = writeBatch(db);
      
      // Eliminar de la sub-colección de la institución
      const courseRef = doc(db, 'educationalInstitutions', institutionId, 'academyCourses', courseId);
      batch.delete(courseRef);
      
      // Actualizar el campo institution del curso a vacío SOLO si existe
      const courseDocRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseDocRef);
      if (courseSnap.exists()) {
        batch.update(courseDocRef, { institution: '' });
      }
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error al eliminar curso de la institución:', error);
      throw error;
    }
  },

  // Agregar un curso a la sub-colección academyCourses
  addAcademyCourse: async (institutionId, courseId) => {
    try {
      const batch = writeBatch(db);
      
      // Obtener el nombre de la institución
      const institutionRef = doc(db, 'educationalInstitutions', institutionId);
      const institutionSnap = await getDoc(institutionRef);
      
      if (!institutionSnap.exists()) {
        throw new Error('Institución no encontrada');
      }
      
      const institutionName = institutionSnap.data().name;
      
      // Obtener el nombre del curso
      const courseRef = doc(db, 'academyCourses', courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (!courseSnap.exists()) {
        throw new Error('Curso no encontrado');
      }
      
      const courseName = courseSnap.data().name;
      
      // Agregar a la sub-colección de la institución
      const institutionCourseRef = doc(db, 'educationalInstitutions', institutionId, 'academyCourses', courseId);
      batch.set(institutionCourseRef, { 
        addedAt: new Date().toISOString(),
        name: courseName
      });
      
      // Actualizar el campo institution del curso
      batch.update(courseRef, { institution: institutionName });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error al agregar curso a la institución:', error);
      throw error;
    }
  },

  // Buscar la institución a la que pertenece un admin por su uid
  getInstitutionByAdminUid: async (adminUid) => {
    const institutionsSnapshot = await getDocs(collection(db, 'educationalInstitutions'));
    for (const institutionDoc of institutionsSnapshot.docs) {
      const adminsRef = collection(db, 'educationalInstitutions', institutionDoc.id, 'admins');
      const adminsSnapshot = await getDocs(adminsRef);
      const found = adminsSnapshot.docs.find(adminDoc => adminDoc.data().uid === adminUid);
      if (found) {
        return { id: institutionDoc.id, ...institutionDoc.data() };
      }
    }
    return null;
  }
};

export default educationalInstitutionsData; 